import {
  BillingCycle,
  DateFormatSetting,
  GetMeDocument,
  GetMeQuery,
  PlanAnalyticsType,
  PlanImporterType,
  PostMultiMediaAttachMode,
  PostQueuePosition,
  StartOfWeekFormatSetting,
  SubscriptionStatus,
  TimeFormatSetting,
  TimezoneSetting,
  useChangeTimezoneMutation,
  useGetMeQuery,
  UserAppRole,
  UserOrganizationRole
} from '../models/graphql';
import { ApolloError, gql } from '@apollo/client';
import { useAuth } from './useAuth';
import { MockedResponse } from '@apollo/client/testing';
import { faker } from '@faker-js/faker';
import { RefetchQueryFunction } from '../models/basic-objects';
import * as Factory from 'factory.ts';
import { useEffect } from 'react';
import {
  GetMe_me,
  GetMe_me_onboarding,
  GetMe_me_organizations,
  GetMe_me_organizations_myUserAssignment,
  GetMe_me_organizations_myUserAssignment_settings,
  GetMe_me_organizations_settings,
  GetMe_me_organizations_workspaces,
  GetMe_me_organizations_workspaces_settings,
  GetMe_me_settings
} from '../models/legacy-graphql';
import { convertTimezoneNameToEnum } from '../helpers/timeFormatter';
import { TimezoneNames } from '../constants/timezones';

gql`
  query GetMe {
    me {
      ...UserFields
      settings {
        id
        onlineMarketing
        notifyFeatureAddedEnabled
        notifyMailingListEnabled
        notifyProductUpdatesEnabled
        notifyReferralCreditsEnabled
      }
      organizations {
        id
        name
        legacyMemberId
        fromLegacy
        myUserAssignment {
          role
          settings {
            id
            dateFormat
            timeFormat
            startOfWeekFormat
          }
        }
        suspended
        spamLocked
        subscription {
          status
          billingCycle
          nextBillingDateUtc
          trialExpiryDateUtc
          upgradedDateUtc
        }
        plan {
          name
          importerType
          analyticsType
          featureFlags
          limits {
            activeCategories
            posts
            socialProfiles
            timeslotsPerWeekdayPerProfile
            autoImports
            importSources
            aiCredits
            users
          }
          usage {
            ...UsageFields
          }
        }
        workspaces {
          id
          name
          timezone
          settings {
            id
            defaultBitly {
              domain
              bitly {
                ...CoreBitlyFields
              }
            }
            evergreenRecycling
            multiMediaAttachMode
            defaultQueuePosition
            queuesPaused
            autoImportsPaused
            shouldRemoveUtm
            campaignTracking
            utm {
              campaign
              content
              medium
              source
              term
            }
          }
        }
        settings {
          id
          productAnalyticsEnabled
        }
        mobileApps {
          ...MobileAppFields
        }
      }
      onboarding {
        ...CoreOnboardingFields
      }
    }
  }
  mutation ChangeTimezone($newTimezone: TimezoneSetting!, $userId: ID) {
    updateUser(input: { timezone: $newTimezone }, userId: $userId) {
      ...UserFields
    }
  }
`;

// Declare this outside of hook scope to share between all instances of useUserData. We only want to call it once per
// window load, and re-rendering is not needed. When the user logs out, we refresh so this will be reset again.
let userTimezoneUpdateCalled = false;

export const factoryGetMe_me_organizations_myUserAssignment_settings =
  Factory.Sync.makeFactory<GetMe_me_organizations_myUserAssignment_settings>({
    __typename: 'UserOrganizationSettings',
    id: Factory.each(i => `${i + 1}`),
    timeFormat: Factory.each(
      () => faker.helpers.arrayElement(Object.getOwnPropertyNames(TimeFormatSetting)) as TimeFormatSetting
    ),
    startOfWeekFormat: StartOfWeekFormatSetting.MONDAY,
    dateFormat: Factory.each(
      () => faker.helpers.arrayElement(Object.getOwnPropertyNames(DateFormatSetting)) as DateFormatSetting
    )
  });

export const factoryGetMe_me_organizations_myUserAssignment =
  Factory.Sync.makeFactory<GetMe_me_organizations_myUserAssignment>({
    __typename: 'UserOrganizationAssignment',
    role: UserOrganizationRole.EDITOR,
    settings: factoryGetMe_me_organizations_myUserAssignment_settings.build()
  });

export const factoryGetMe_me_organizations_workspaces_settings =
  Factory.Sync.makeFactory<GetMe_me_organizations_workspaces_settings>({
    __typename: 'WorkspaceSettings',
    id: Factory.each(i => `${i + 1}`),
    evergreenRecycling: Factory.each(() => faker.datatype.boolean()),
    multiMediaAttachMode: Factory.each(
      () => faker.helpers.arrayElement(Object.getOwnPropertyNames(PostMultiMediaAttachMode)) as PostMultiMediaAttachMode
    ),
    defaultQueuePosition: faker.helpers.arrayElement(
      Object.getOwnPropertyNames(PostQueuePosition)
    ) as PostQueuePosition,
    queuesPaused: Factory.each(() => faker.datatype.boolean()),
    autoImportsPaused: Factory.each(() => faker.datatype.boolean()),
    shouldRemoveUtm: Factory.each(() => faker.datatype.boolean()),
    campaignTracking: Factory.each(() => faker.datatype.boolean()),
    defaultBitly: null,
    utm: {
      source: 'SOURCE',
      medium: 'MEDIUM',
      campaign: 'CAMPAIGN',
      term: 'TERM',
      content: 'CONTENT'
    }
  });

export const factoryGetMe_me_organizations_workspaces = Factory.Sync.makeFactory<GetMe_me_organizations_workspaces>({
  __typename: 'Workspace',
  id: Factory.each(i => `${i + 1}`),
  name: Factory.each(() => faker.commerce.productName()),
  timezone: Factory.each(
    () => faker.helpers.arrayElement(Object.getOwnPropertyNames(TimezoneSetting)) as TimezoneSetting
  ),
  settings: Factory.each(() => factoryGetMe_me_organizations_workspaces_settings.build())
});

export const factoryGetMe_me_organizations_settings = Factory.Sync.makeFactory<GetMe_me_organizations_settings>({
  __typename: 'OrganizationSettings',
  id: Factory.each(i => `${i + 1}`),
  productAnalyticsEnabled: Factory.each(() => faker.datatype.boolean())
});

export const factoryGetMe_me_organizations = Factory.Sync.makeFactory<GetMe_me_organizations>({
  __typename: 'Organization',
  id: Factory.each(i => `${i + 1}`),
  legacyMemberId: Factory.each(i => `${i + 1}`),
  fromLegacy: false,
  myUserAssignment: Factory.each(() => factoryGetMe_me_organizations_myUserAssignment.build()),
  name: Factory.each(() => faker.company.name()),
  settings: Factory.each(() => factoryGetMe_me_organizations_settings.build()),
  workspaces: Factory.each(() => factoryGetMe_me_organizations_workspaces.buildList(1)),
  mobileApps: [],
  spamLocked: false,
  subscription: {
    status: SubscriptionStatus.PAID,
    billingCycle: BillingCycle.MONTHLY,
    trialExpiryDateUtc: '2021-12-01 00:00:00',
    nextBillingDateUtc: '2022-01-01 00:00:00',
    upgradedDateUtc: '2021-12-01 00:00:00',
    __typename: 'OrganizationSubscriptionType'
  },
  suspended: false,
  plan: {
    name: 'Agency',
    analyticsType: PlanAnalyticsType.ADVANCED_ANALYTICS,
    featureFlags: [],
    limits: {
      posts: 99,
      activeCategories: 99,
      users: 99,
      autoImports: 99,
      socialProfiles: 99,
      timeslotsPerWeekdayPerProfile: 99,
      importSources: 99,
      aiCredits: 99
    },
    importerType: PlanImporterType.ADVANCED_IMPORTER,
    usage: {
      __typename: 'PlanUsageType',
      posts: 5,
      activeCategories: 5,
      users: 5,
      autoImports: 5,
      socialProfiles: 5,
      importSources: 5,
      aiCredits: 5
    }
  }
});

export const factoryGetMe_me_settings = Factory.Sync.makeFactory<GetMe_me_settings>({
  __typename: 'UserSettings',
  id: Factory.each(i => `${i + 1}`),
  onlineMarketing: Factory.each(() => faker.datatype.boolean()),
  notifyMailingListEnabled: Factory.each(() => faker.datatype.boolean()),
  notifyProductUpdatesEnabled: Factory.each(() => faker.datatype.boolean()),
  notifyFeatureAddedEnabled: Factory.each(() => faker.datatype.boolean()),
  notifyReferralCreditsEnabled: Factory.each(() => faker.datatype.boolean())
});

export const factoryGetMe_me_onboarding = Factory.Sync.makeFactory<GetMe_me_onboarding>({
  __typename: 'UserOnboardingType',
  version: Factory.each(() => faker.datatype.number({ min: 1, max: 3 })),
  completed: Factory.each(() => faker.datatype.boolean()),
  stepsSeen: []
});

export const factoryGetMe_me = Factory.Sync.makeFactory<GetMe_me>({
  __typename: 'User',
  passwordSet: false,
  id: Factory.each(i => `${i + 1}`),
  firstName: Factory.each(() => faker.name.firstName()),
  email: Factory.each(() => faker.internet.email()),
  appRole: UserAppRole.USER,
  organizations: Factory.each(() => factoryGetMe_me_organizations.buildList(1)),
  settings: Factory.each(() => factoryGetMe_me_settings.build()),
  timezone: convertTimezoneNameToEnum(
    Intl.DateTimeFormat().resolvedOptions().timeZone as TimezoneNames
  ) as TimezoneSetting,
  onboarding: factoryGetMe_me_onboarding.build()
});

export const UserDataMock: MockedResponse<GetMeQuery>[] = [
  {
    request: {
      query: GetMeDocument
    },
    result: {
      data: {
        me: factoryGetMe_me.build()
      }
    }
  }
];

export const UserMocksWithWorkspaceId: MockedResponse<GetMeQuery>[] = [
  {
    request: {
      query: GetMeDocument
    },
    result: {
      data: {
        me: factoryGetMe_me.build({
          appRole: UserAppRole.ADMIN,
          organizations: factoryGetMe_me_organizations.buildList(1, {
            workspaces: factoryGetMe_me_organizations_workspaces.buildList(1, { id: '1' })
          }),
          settings: factoryGetMe_me_settings.build()
        })
      }
    }
  }
];

export type UserData = {
  loading: boolean;
  error: ApolloError;
  userData?: GetMe_me;
  refetch: RefetchQueryFunction<GetMeQuery>;
};

export const useUserData = (): UserData => {
  const { isLoggedIn } = useAuth();
  const { loading, data, error, refetch } = useGetMeQuery({ skip: !isLoggedIn });
  const [changeTimezone] = useChangeTimezoneMutation();
  const userBrowserTimezone = convertTimezoneNameToEnum(
    Intl.DateTimeFormat().resolvedOptions().timeZone as TimezoneNames
  ) as TimezoneSetting;
  const userData = data?.me;

  useEffect(() => {
    if (!userTimezoneUpdateCalled && userData && userBrowserTimezone && userData.timezone !== userBrowserTimezone) {
      userTimezoneUpdateCalled = true;
      changeTimezone({ variables: { userId: userData.id, newTimezone: userBrowserTimezone } });
    }
  }, [userData, changeTimezone, userBrowserTimezone]);

  return {
    userData: (data?.me as GetMe_me) || null,
    loading: loading,
    error: error as ApolloError,
    refetch
  };
};
